// Shared schema.org JSON-LD building blocks for KnowMySchemes
// Reused across public pages for Organization, WebSite, breadcrumbs etc.

export const SITE_URL = 'https://knowmyschemes.com';

export const ORG_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'KnowMySchemes',
  alternateName: 'KMS',
  url: SITE_URL,
  logo: `${SITE_URL}/logo512.png`,
  description:
    'KnowMySchemes is a privately operated digital platform that helps Indian citizens discover central and state government welfare schemes, browse live government job notifications, and get AI-powered policy impact analysis.',
  areaServed: { '@type': 'Country', name: 'India' },
  contactPoint: [
    {
      '@type': 'ContactPoint',
      contactType: 'Customer Support',
      email: 'support@knowmyschemes.com',
      areaServed: 'IN',
      availableLanguage: ['English', 'Hindi', 'Telugu', 'Tamil'],
    },
    {
      '@type': 'ContactPoint',
      contactType: 'Grievance Officer',
      email: 'info@knowmyschemes.com',
      areaServed: 'IN',
    },
  ],
  sameAs: [
    'https://play.google.com/store/apps/details?id=com.knowmyschemes',
    'https://apps.apple.com/app/knowmyschemes',
  ],
};

export const WEBSITE_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'KnowMySchemes',
  url: SITE_URL,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_URL}/schemes?search={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

export const breadcrumbJsonLd = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    item: it.url,
  })),
});

// JobPosting schema for an individual government job notification.
// Always sets directApply:false because applications happen on the official
// government recruitment portal — not on KnowMySchemes.
export const jobPostingJsonLd = (job) => {
  if (!job || !job.title) return null;
  const jobId = job.id || job.job_id;
  if (!jobId) return null;

  const detailUrl = `${SITE_URL}/schemes?tab=jobs&job=${encodeURIComponent(jobId)}`;
  const applyUrl = job.apply_url || job.notification_url || job.source_url || detailUrl;

  // Extract YYYY-MM-DD from last_date / posted_at when present
  const toIsoDate = (raw) => {
    if (!raw) return undefined;
    try {
      const d = new Date(raw);
      if (isNaN(d.getTime())) return undefined;
      return d.toISOString().split('T')[0];
    } catch {
      return undefined;
    }
  };
  const datePosted = toIsoDate(job.posted_at || job.created_at) || new Date().toISOString().split('T')[0];
  const validThrough = toIsoDate(job.last_date);

  const description =
    `${job.title} — ${job.organisation || job.department || 'Government of India'}. ` +
    `Qualification: ${job.qualification || 'See official notification'}. ` +
    `${job.vacancies ? `Vacancies: ${job.vacancies}. ` : ''}` +
    `Location: ${job.location || 'India'}. ` +
    `This is an aggregated informational listing on KnowMySchemes — apply only on the official government recruitment portal linked above.`;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: job.title,
    description,
    identifier: {
      '@type': 'PropertyValue',
      name: job.organisation || job.department || 'Government of India',
      value: jobId,
    },
    datePosted,
    employmentType: 'FULL_TIME',
    hiringOrganization: {
      '@type': 'Organization',
      name: job.organisation || job.department || 'Government of India',
      sameAs: job.source_url || undefined,
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: job.location || 'India',
        addressCountry: 'IN',
      },
    },
    applicantLocationRequirements: { '@type': 'Country', name: 'India' },
    url: detailUrl,
    directApply: false, // Applications happen on the official government portal
  };

  if (validThrough) schema.validThrough = validThrough;

  if (job.salary_min || job.salary_max) {
    schema.baseSalary = {
      '@type': 'MonetaryAmount',
      currency: 'INR',
      value: {
        '@type': 'QuantitativeValue',
        minValue: job.salary_min || undefined,
        maxValue: job.salary_max || undefined,
        unitText: 'MONTH',
      },
    };
  }

  if (job.qualification && job.qualification !== 'Various') {
    schema.educationRequirements = {
      '@type': 'EducationalOccupationalCredential',
      credentialCategory: job.qualification,
    };
  }

  return schema;
};

